/**
 * Typed HTTP client that attaches Bearer tokens from MSAL and handles errors.
 */

import { type IPublicClientApplication } from "@azure/msal-browser";
import { apiScopes } from "./msal-config";

/**
 * Structured API error with status code and server message.
 */
export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly serverMessage: string,
    public readonly correlationId: string | null,
  ) {
    super(`API Error ${statusCode}: ${serverMessage}`);
    this.name = "ApiError";
  }
}

interface ApiErrorResponse {
  readonly statusCode: number;
  readonly error: string;
  readonly message: string;
  readonly correlationId?: string;
}

interface ApiClientOptions {
  readonly msalInstance: IPublicClientApplication;
  readonly baseUrl: string;
}

interface RequestOptions {
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly body?: unknown;
  readonly headers?: Record<string, string>;
  readonly signal?: AbortSignal;
}

/**
 * Create an API client instance bound to an MSAL PublicClientApplication.
 * Automatically acquires and attaches Bearer tokens for authenticated requests.
 */
export function createApiClient(options: ApiClientOptions): {
  get: <T>(path: string, requestOptions?: RequestOptions) => Promise<T>;
  post: <T>(path: string, body: unknown, requestOptions?: RequestOptions) => Promise<T>;
  put: <T>(path: string, body: unknown, requestOptions?: RequestOptions) => Promise<T>;
  patch: <T>(path: string, body: unknown, requestOptions?: RequestOptions) => Promise<T>;
  del: <T>(path: string, requestOptions?: RequestOptions) => Promise<T>;
} {
  const { msalInstance, baseUrl } = options;

  async function getAccessToken(): Promise<string> {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new ApiError(401, "No authenticated account found", null);
    }

    const account = accounts[0];
    if (!account) {
      throw new ApiError(401, "No authenticated account found", null);
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: [...apiScopes],
        account,
      });
      return response.accessToken;
    } catch {
      // Silent acquisition failed; trigger interactive login
      await msalInstance.acquireTokenRedirect({
        scopes: [...apiScopes],
        account,
      });
      throw new ApiError(401, "Token acquisition requires interaction", null);
    }
  }

  async function request<T>(path: string, requestOptions: RequestOptions = {}): Promise<T> {
    const token = await getAccessToken();
    const url = `${baseUrl}${path}`;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      ...requestOptions.headers,
    };

    const fetchOptions: RequestInit = {
      method: requestOptions.method ?? "GET",
      headers,
      signal: requestOptions.signal,
    };

    if (requestOptions.body !== undefined) {
      headers["Content-Type"] = "application/json";
      fetchOptions.body = JSON.stringify(requestOptions.body);
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      let errorBody: ApiErrorResponse | null = null;
      try {
        errorBody = (await response.json()) as ApiErrorResponse;
      } catch {
        // Response body was not valid JSON
      }

      throw new ApiError(
        response.status,
        errorBody?.message ?? response.statusText,
        errorBody?.correlationId ?? null,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  return {
    get: <T>(path: string, requestOptions?: RequestOptions) =>
      request<T>(path, { ...requestOptions, method: "GET" }),
    post: <T>(path: string, body: unknown, requestOptions?: RequestOptions) =>
      request<T>(path, { ...requestOptions, method: "POST", body }),
    put: <T>(path: string, body: unknown, requestOptions?: RequestOptions) =>
      request<T>(path, { ...requestOptions, method: "PUT", body }),
    patch: <T>(path: string, body: unknown, requestOptions?: RequestOptions) =>
      request<T>(path, { ...requestOptions, method: "PATCH", body }),
    del: <T>(path: string, requestOptions?: RequestOptions) =>
      request<T>(path, { ...requestOptions, method: "DELETE" }),
  };
}
